import * as Yup from 'yup';

import { AdminTokensEndpoints, AdminUserEndpoints, fetcher, useAdminGames, useAdminOwnerQuests, useAdminTournaments } from '@olagg/api-hooks'
import { Box, Button, Flex, Select, Spinner, Switch, Text, VStack } from '@chakra-ui/react'
import { FC, useCallback, useEffect, useState } from 'react'
import { FormMiniGameData, formMiniGameSchema } from '@olagg/validation-schemas/miniGame.schemas'
import { TokenData, TournamentOrganizerData, TreasuresGame, BlockchainNetwork } from '@olagg/db-types'
import { convertUTCToLocalISOString, toLocalTimezone } from '../../utils';

import OlaggForm from '../../components/Forms/OlaggForm'
import OlaggFormInput from '../../components/Forms/OlaggFormInput'
import OlaggFormSelect from '../../components/Forms/OlaggFormSelect'
import OlaggMarkdown from '@olagg/ui-kit/OlaggMarkdown'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import useBlockchainTokenCatalog from '../BlockchainTokenCatalog/hooks/useBlockchainTokenCatalog';
import useBlockchainNetwork from '../BlockchainTokenCatalog/hooks/useBlockchainNetwork';

type Instructions = {
  es?: string;
  en?: string;
  pt?: string;
}

interface IMiniGameFormProps {
  onSubmit: (dataForm: FormMiniGameData) => void;
  miniGame?: TreasuresGame;
};

const buildMiniGameInstructions = (miniGame?: TreasuresGame) => ({
  es: miniGame?.instructions || '',
  en: miniGame?.extraData?.en?.instructions || '',
  pt: miniGame?.extraData?.pt?.instructions || ''
}
)

const MiniGameForm: FC<IMiniGameFormProps> = ({ onSubmit, miniGame }) => {
  const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [otherLangs, setOtherLangs] = useState<boolean>(miniGame?.extraData?.translated || false);
  const [staticOrganizers, setStaticOrganizers] = useState<any[]>([]);
  const [organizer, setOrganizer] = useState();
  const [miniGameInstructions, setMiniGameInstructions] = useState<Instructions>(buildMiniGameInstructions(miniGame));
  const networksArr = ['polygon', 'ronin', 'ethereum']
  const { fetchOrganizers } = useAdminTournaments()
  const [isLoading, setIsLoading] = useState(false)
  const [filteredUsers, setFilteredUsers] = useState([])

  const { all, blockchainTokens } = useBlockchainTokenCatalog();
  const { all: allNetworks, blockchainNetworks } = useBlockchainNetwork();

  const [selectedNetwork, setSelectedNetwork] = useState<string | null>(null);
  const handleNetworkChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedNetwork(event.target.value);
  };
  const filteredBlockchainTokens = selectedNetwork
    ? blockchainTokens?.filter(token => token.network.name === selectedNetwork)
    : blockchainTokens;

  const useFormMethods = useForm<FormMiniGameData>({
    resolver: yupResolver(formMiniGameSchema),
    mode: 'onChange',
    defaultValues: {
      ...miniGame,
      visible: miniGame?.visible ?? false,
      autoRestart: miniGame?.autoRestart ?? false,
    },
  });

  const { owners, asyncOwnerOptions } = useAdminOwnerQuests({ page: 1, limit: 10 });

  const staticOwnerOptions = owners?.map(owner => ({
    value: owner.id,
    label: owner.name
  }));

  const getUsers = useCallback(async () => {
    setIsLoading(true);
    await fetcher(
      AdminUserEndpoints.all({ page: 0, limit: 10 })
    ).then((response) => {
      const { users } = response;
      setFilteredUsers(users.map(user => ({
        value: user.id,
        label: `${user.name} (${user.email})`
      })));
    });

    setIsLoading(false);
  }, []);

  const handleSearchUser = async (inputValue) => {
    return await fetcher(
      AdminUserEndpoints.all({ page: 0, limit: 10, query: inputValue })
    ).then((response) => {
      const { users } = response;
      return users.map(user => ({
        value: user.id,
        label: `${user.name} (${user.email})`
      }));
    });
  };

  const convertToLocalTimeZone = () => {
    // Convert timezones
    if (!miniGame) return;

    const miniGameData: FormMiniGameData = { ...miniGame };

    miniGameData.startTime = toLocalTimezone(miniGameData.startTime);
    miniGameData.endTime = toLocalTimezone(miniGameData.endTime);
    useFormMethods.reset(miniGameData);
  }


  useEffect(() => {
    convertToLocalTimeZone()
    setSelectedNetwork(miniGame?.totalPrize?.network)
  }, [miniGame])

  useEffect(() => {
    getUsers();
    all({
      page: 1,
      limit: 100
    });
    allNetworks({ page: 1, limit: 100 });
  }, [])

  const beforeSubmit = useFormMethods.handleSubmit((formValues) => {
    const sanitizedValues = {
      ...formValues,
      coverImageUrl: formValues.coverImageUrl ?? undefined,
      mapImageUrl: formValues.mapImageUrl ?? undefined,
      tilesImageUrl: formValues.tilesImageUrl ?? undefined,
    };

    onSubmit(sanitizedValues);
  });

  const hasErrors = Object.keys(useFormMethods.formState.errors).length > 0;

  return (
    <OlaggForm
      useFormMethods={useFormMethods}
      onSubmit={beforeSubmit}
      style={{
        width: '100%'
      }}
    >
      <VStack color={'white'} w={'full'} maxW='600px' margin='0 auto'>
        <Flex
          w="full"
          justify="start"
          align="center"
          style={{ marginTop: '30px' }}
        >
          <label
            htmlFor="enableShowLocale"
            style={{ cursor: 'pointer' }}
          >
            Otros idiomas
          </label>
          <Switch
            id="enableShowLocale"
            colorScheme="purple"
            isChecked={otherLangs}
            size="md"
            ml={2}
            onChange={() => setOtherLangs(prev => !prev)}
          />
        </Flex>
        <Box w='full'>
          <OlaggFormInput
            label='Título'
            controlName='title'
            required={true}
          />
          {otherLangs && <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} >
            <OlaggFormInput
              label='Título Inglés'
              controlName='extraData.en.title'
            />
            <OlaggFormInput
              label='Título Portugués'
              controlName='extraData.pt.title'
            />
          </Flex>}

          <OlaggFormInput
            label='Descripción'
            controlName='lead'
            required={true}
          />
          {otherLangs && <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} >
            <OlaggFormInput
              label='Descripción Inglés'
              controlName='extraData.en.lead'
            />
            <OlaggFormInput
              label='Descripción Portugués'
              controlName='extraData.pt.lead'
            />
          </Flex>}

          <OlaggFormInput
            label='Instrucciones'
            controlName='instructions'
            description='Escribe las instrucciones para el mini juego'
            inputType='textarea'
            required={true}
            onChange={(e) => setMiniGameInstructions({ ...miniGameInstructions, es: e.target.value })}
          />

          <Text lineHeight='normal' fontWeight={600} mb='2'>
            Instrucciones (Vista previa)
          </Text>
          <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
            <OlaggMarkdown>
              {miniGameInstructions.es || miniGameInstructions?.instructions || ''}
            </OlaggMarkdown>
          </Box>

          {otherLangs && <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} >
            <OlaggFormInput
              label='Instrucciones Inglés'
              controlName='extraData.en.instructions'
              onChange={(e) => setMiniGameInstructions({ ...miniGameInstructions, en: e.target.value })}
            />
            <Text lineHeight='normal' fontWeight={600} mb='2'>
              Instrucciones Inglés (Vista previa)
            </Text>
            <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
              <OlaggMarkdown>
                {miniGameInstructions.en || miniGame?.extraData?.en?.instructions}
              </OlaggMarkdown>
            </Box>
            <OlaggFormInput
              label='Instrucciones Portugués'
              controlName='extraData.pt.instructions'
              onChange={(e) => setMiniGameInstructions({ ...miniGameInstructions, pt: e.target.value })}
            />
            <Text lineHeight='normal' fontWeight={600} mb='2'>
              Instrucciones Portugués (Vista previa)
            </Text>
            <Box w='full' mb='5' border='1px solid white' minH='10' borderRadius='5' p='3'>
              <OlaggMarkdown>
                {miniGameInstructions.pt || miniGame?.extraData?.pt?.instructions}
              </OlaggMarkdown>
            </Box>

          </Flex>}

          <OlaggFormInput
            label='Slug'
            controlName='slug'
            required
          />

          <OlaggFormSelect
            newVersion
            label='Owner'
            controlName='ownerId'
            required
            isSearchable
            staticOptions={staticOwnerOptions}
            asyncOptions={asyncOwnerOptions}
          />

          <Text fontSize={'18px'} fontWeight='700'>Blockchain Token Network Filter</Text>
          <Select value={selectedNetwork} style={{ marginBottom: '10px' }} placeholder="Select network" onChange={handleNetworkChange}>
            {
              blockchainNetworks?.map(network => (
                <option key={network.id} value={network.name}>{network.name}</option>
              ))
            }
          </Select>
          {selectedNetwork && (
            <>
              <Text fontSize={'18px'} fontWeight='700'>Blockchain Token</Text>
              <OlaggFormSelect
                controlName={'blockchainTokenId'}
                staticOptions={filteredBlockchainTokens?.map(t => ({
                  value: t.id,
                  label: t.name
                }))}
                required={true}
                newVersion
                isSearchable
              />
            </>
          )}

          {isLoading ? (
            <Spinner />
          ) : (
            <OlaggFormSelect
              controlName="betaTesterIds"
              label="Usuarios BETA testers"
              description="Elige uno o mas usuarios"
              staticOptions={filteredUsers}
              asyncOptions={handleSearchUser}
              multiple={true}
              placeholder="Buscar por nombre, email o id"
              isSearchable={true}
              required={false}
              onChange={(selectedOptions) => {
                const ids = selectedOptions.map(option => option.value);
                useFormMethods.setValue('betaTesterIds', ids);
              }}
            />
          )}
          <OlaggFormInput
            label='Cantidad'
            controlName='totalPrize[amount]'
            placeholder='Cantidad'
            required={true}
          />
          <Text fontSize={'16px'} fontWeight='700'>Blockchain Token Network Filter</Text>
          <Select value={selectedNetwork} placeholder="Select network" onChange={handleNetworkChange} style={{ marginBottom: '10px' }}>
            {
              blockchainNetworks?.map(network => (
                <option key={network.id} value={network.name}>{network.name}</option>
              ))
            }
          </Select>

          {selectedNetwork && (
            <OlaggFormSelect
              label='Blockchain Token'
              controlName='blockchainTokenId'
              staticOptions={filteredBlockchainTokens?.map(blockchainToken => ({
                value: blockchainToken.id,
                label: blockchainToken.name
              }))}
              required={true}
              newVersion
            />
          )}

          <OlaggFormInput
            label='Imagen de portada'
            controlName='coverImageUrl'
            description='URL de la imagen para mostrar en pre-visualización.'
          />
          {otherLangs && <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} >
            <OlaggFormInput
              label='Imagen de portada Inglés'
              controlName='extraData.en.coverImageUrl'
            />
            <OlaggFormInput
              label='Imagen de portada Portugués'
              controlName='extraData.pt.coverImageUrl'
            />

          </Flex>}

          <OlaggFormInput
            label='Imagen del mapa'
            controlName='mapImageUrl'
            description='URL de la imagen del mapa.'
            required={false}
          />
          {otherLangs && <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} >
            <OlaggFormInput
              label='Imagen del mapa Inglés'
              controlName='extraData.en.mapImageUrl'
            />
            <OlaggFormInput
              label='Imagen del mapa Portugués'
              controlName='extraData.pt.mapImageUrl'
            />

          </Flex>}

          <OlaggFormInput
            label='Imagen de tiles'
            controlName='tilesImageUrl'
            description='Debe ser una imagen de las mismas dimensiones del mapa. Esta imagen se sobrepone al mapa y se muestra como el background de todos los Tiles aun no descubiertos.'
            required={false}
          />
          {otherLangs && <Flex direction='column' w='full' bgColor='#20173d' p='8' borderRadius={'12px'} mb={4} >
            <OlaggFormInput
              label='Imagen de tiles Inglés'
              controlName='extraData.en.tilesImageUrl'
            />
            <OlaggFormInput
              label='Imagen de tiles Portugués'
              controlName='extraData.pt.tilesImageUrl'
            />

          </Flex>}

          <OlaggFormInput
            label='Número de filas'
            controlName='rows'
            description='Debe ser menor o igual que el height de la imagen del mapa dividido entre 50 px'
            required={true}
            disabled={miniGame?.status === 'active' ? true : false}
          />

          <OlaggFormInput
            label='Número de columnas'
            controlName='columns'
            description='Debe ser menor que el width de la imagen del mapa dividido entre 50 px'
            required={true}
            disabled={miniGame?.status === 'active' ? true : false}
          />

          <OlaggFormInput
            label='Tiempo de espera'
            controlName='waitTime'
            description='Tiempo en segundos a esperar entre cada oportunidad.'
            required={true}
          />

          <OlaggFormInput
            label='Umbral mínimo de XP'
            controlName='minXpThreshold'
            description='Cuantos XP debe acumular como minimo durante el tiempo de espera para poder tener una oportunidad.'
            required={true}
          />

          <OlaggFormInput
            label='Exigir umbral de XP en la primera jugada'
            description='Si se activa, el jugador deberá sumar el umbral mínimo de XP antes de poder realizar su primera jugada. Se toman como base los XP del usuario al momento de iniciar la partida.'
            controlName='requireXpOnFirstMovement'
            inputType='switch'
            required={false}
          />

          <OlaggFormInput
            label='Level mínimo para participar'
            controlName='minLevel'
            description='El nivel mínimo que debe tener para participar.'
            required={true}
          />

          <OlaggFormInput
            label='Level máximo para participar'
            controlName='maxLevel'
            description='El nivel máximo que debe tener para poder participar (dejar en blanco para que no haya límite).'
          />

          <OlaggFormInput
            label='Fecha de inicio del mini juego'
            controlName='startTime'
            inputType='datetime'
            description={`Zona horaria: ${currentTimeZone}`}
            datePickerProps={{ backgroundColor: 'white' }}
            required={true}
          />

          <OlaggFormInput
            label='Fecha de finalización del mini juego'
            controlName='endTime'
            inputType='datetime'
            description={`Zona horaria: ${currentTimeZone}`}
            datePickerProps={{ backgroundColor: 'white' }}
            required={true}
          />

          <OlaggFormInput
            label='Cantidad de tesoros'
            controlName='totalTreasures'
            description='Debe ser menor que el total de número de tiles.'
            required={true}
            disabled={miniGame?.status === 'active' ? true : false}
          />

          <OlaggFormInput
            label='Visible'
            controlName='visible'
            inputType='switch'
            required={false}
          />

          <OlaggFormInput
            label='Reiniciar partida cuando termina'
            controlName='autoRestart'
            inputType='switch'
            required={false}
            disabled={miniGame ? !miniGame.autoRestart : false}
          />
        </Box>

        <Button
          onClick={beforeSubmit}
          variant={'filledGradient'}
          type="button"
          w={'full'}
          style={{
            marginTop: '30px'
          }}
          isDisabled={hasErrors}
        >
          Guardar
        </Button>

      </VStack>
    </OlaggForm>
  )
}

export default MiniGameForm;
